import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  ArcElement,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LineElement,
  LinearScale,
  PointElement,
  Title as ChartTitle,
  Tooltip
} from "chart.js";
import { Bar, Doughnut, Line } from "react-chartjs-2";
import html2pdf from "html2pdf.js";
import moment from "moment";
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import AccessTimeIcon from "@material-ui/icons/AccessTime";
import AssessmentIcon from "@material-ui/icons/Assessment";
import AutorenewIcon from "@material-ui/icons/Autorenew";
import ChatIcon from "@material-ui/icons/Chat";
import CloudDownloadIcon from "@material-ui/icons/CloudDownload";
import EqualizerIcon from "@material-ui/icons/Equalizer";
import ForumIcon from "@material-ui/icons/Forum";
import GroupIcon from "@material-ui/icons/Group";
import PersonIcon from "@material-ui/icons/Person";
import PictureAsPdfIcon from "@material-ui/icons/PictureAsPdf";
import PhoneInTalkIcon from "@material-ui/icons/PhoneInTalk";
import ScheduleIcon from "@material-ui/icons/Schedule";
import TimelineIcon from "@material-ui/icons/Timeline";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import ForbiddenPage from "../../components/ForbiddenPage";
import { UsersFilter } from "../../components/UsersFilter";
import { ConnectionsFilter } from "../../components/ConnectionsFilter";
import { QueueFilter } from "../../components/QueueFilter";
import { AuthContext } from "../../context/Auth/AuthContext";
import api from "../../services/api";
import toastError from "../../errors/toastError";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  ChartTitle,
  Tooltip,
  Legend
);

const useStyles = makeStyles(theme => ({
  mainContainerScroll: {
    maxHeight: "calc(100vh - 200px)",
    overflowY: "auto",
    paddingRight: theme.spacing(1),
    "&::-webkit-scrollbar": {
      width: "8px"
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: "rgba(0, 0, 0, 0.2)",
      borderRadius: "4px"
    }
  },
  mainPaper: {
    padding: theme.spacing(2),
    borderRadius: 16,
    background: theme.palette.fancyBackground || theme.palette.background.paper,
    marginBottom: theme.spacing(2)
  },
  filtersPaper: {
    padding: theme.spacing(2),
    borderRadius: 16,
    marginBottom: theme.spacing(2)
  },
  card: {
    borderRadius: 16,
    height: "100%",
    boxShadow: "0 8px 24px rgba(0,0,0,0.08)"
  },
  metricValue: {
    fontWeight: 700,
    marginTop: theme.spacing(1)
  },
  metricLabel: {
    color: theme.palette.text.secondary
  },
  chartPaper: {
  padding: theme.spacing(2),
  borderRadius: 16,
  height: "100%",
  breakInside: "avoid",
  pageBreakInside: "avoid"
},
  chartBox: {
  position: "relative",
  height: 450
},
  sectionTitle: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    marginBottom: theme.spacing(1),
    fontWeight: 600
  },
  tablePaper: {
    borderRadius: 16,
    overflow: "hidden",
    height: "100%"
  },
  tableWrapper: {
    overflowX: "auto"
  },
  helperText: {
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(1)
  },
  iconBadge: {
    width: 48,
    height: 48,
    borderRadius: 14,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "rgba(63,81,181,0.12)",
    color: theme.palette.primary.main
  },
  loadingBox: {
    padding: theme.spacing(6),
    display: "flex",
    justifyContent: "center"
  },
  emptyBox: {
    padding: theme.spacing(4),
    textAlign: "center",
    color: theme.palette.text.secondary
  },
  insightCard: {
    borderRadius: 16,
    padding: theme.spacing(2),
    background: "linear-gradient(135deg, rgba(63,81,181,0.08), rgba(0,188,212,0.08))",
    border: "1px solid rgba(63,81,181,0.1)"
  },
  insightLine: {
    marginTop: theme.spacing(1),
    color: theme.palette.text.secondary,
    lineHeight: 1.7
  }
}));

const emptyAnalytics = {
  summary: {
    totalMessages: 0,
    inboundMessages: 0,
    outboundMessages: 0,
    activeContacts: 0,
    activeConnections: 0,
    activeTickets: 0,
    ticketsCreated: 0,
    openTickets: 0,
    pendingTickets: 0,
    closedTickets: 0,
    avgMessagesPerTicket: 0,
    avgFirstResponseMinutes: 0,
    avgWaitMinutes: 0,
    avgResolutionMinutes: 0,
    peakHourLabel: "--",
    peakHourMessages: 0,
    peakWeekdayLabel: "--",
    peakWeekdayTickets: 0
  },
  messagesByDay: [],
  ticketsByStatus: [],
  topContacts: [],
  topReceivingConnections: [],
  topSendingConnections: [],
  topUsers: [],
  hourlyDistribution: [],
  weekdayRanking: [],
  ticketsByQueue: [],
  usersComparison: [],
  responseTimeBuckets: [],
  conversationSizeBuckets: []
};

const MetricCard = ({ icon, label, value, helper }) => {
  const classes = useStyles();

  return (
    <Card className={classes.card} variant="outlined">
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="body2" className={classes.metricLabel}>
              {label}
            </Typography>
            <Typography variant="h4" className={classes.metricValue}>
              {value}
            </Typography>
          </Box>
          <Box className={classes.iconBadge}>{icon}</Box>
        </Box>
        {helper ? (
          <Typography variant="body2" className={classes.helperText}>
            {helper}
          </Typography>
        ) : null}
      </CardContent>
    </Card>
  );
};

const RankingTable = ({ title, subtitle, columns, rows, emptyText = "Nenhum dado encontrado no período." }) => {
  const classes = useStyles();

  return (
    <Paper className={classes.tablePaper} variant="outlined">
      <Box p={2}>
        <Typography variant="h6">{title}</Typography>
        {subtitle ? (
          <Typography variant="body2" color="textSecondary">
            {subtitle}
          </Typography>
        ) : null}
      </Box>
      <Divider />
      {rows.length === 0 ? (
        <Box className={classes.emptyBox}>{emptyText}</Box>
      ) : (
        <Box className={classes.tableWrapper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                {columns.map(column => (
                  <TableCell key={column.field}>{column.title}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row, index) => (
                <TableRow key={`${title}-${index}`}>
                  {columns.map(column => (
                    <TableCell key={column.field}>
                      {typeof column.render === "function"
                        ? column.render(row, index)
                        : row[column.field]}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}
    </Paper>
  );
};

const chartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  animation: false,
  plugins: {
    legend: {
      position: "top"
    }
  }
};

const formatInteger = value => Number(value || 0).toLocaleString("pt-BR");

const formatDecimal = (value, digits = 1) =>
  Number(value || 0).toLocaleString("pt-BR", {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits
  });

const formatPercent = value => `${formatDecimal(value, 1)}%`;

const formatMinutes = value => {
  const totalMinutes = Number(value || 0);

  if (!Number.isFinite(totalMinutes) || totalMinutes <= 0) {
    return "0 min";
  }

  if (totalMinutes < 1) {
    return `${Math.max(1, Math.round(totalMinutes * 60))} s`;
  }

  if (totalMinutes < 60) {
    return `${formatDecimal(totalMinutes, 1)} min`;
  }

  const days = Math.floor(totalMinutes / 1440);
  const hours = Math.floor((totalMinutes % 1440) / 60);
  const minutes = Math.round(totalMinutes % 60);

  if (days > 0) {
    return `${days}d ${hours}h ${minutes}m`;
  }

  return `${hours}h ${minutes}m`;
};

const escCsv = value => {
  const safeValue = value === null || value === undefined ? "" : String(value);
  return `"${safeValue.replace(/"/g, '""')}"`;
};

const Analitico = () => {
  const classes = useStyles();
  const printableRef = useRef(null);
  const { user } = useContext(AuthContext);

  const [dateFrom, setDateFrom] = useState(moment().subtract(29, "days").format("YYYY-MM-DD"));
  const [dateTo, setDateTo] = useState(moment().format("YYYY-MM-DD"));
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [selectedQueues, setSelectedQueues] = useState([]);
  const [selectedConnections, setSelectedConnections] = useState([]);
  const [loading, setLoading] = useState(false);
  const [analytics, setAnalytics] = useState(emptyAnalytics);

  const fetchAnalytics = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/analytics/overview", {
        params: {
          dateFrom,
          dateTo,
          userIds: selectedUsers.join(","),
          queueIds: selectedQueues.join(","),
          whatsappIds: selectedConnections.join(",")
        }
      });

      setAnalytics({ ...emptyAnalytics, ...data });
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, selectedConnections, selectedQueues, selectedUsers]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  const orderedWeekdays = useMemo(
    () => [...analytics.weekdayRanking].sort((a, b) => Number(a.isoDay || 0) - Number(b.isoDay || 0)),
    [analytics.weekdayRanking]
  );

  const topQueue = useMemo(() => analytics.ticketsByQueue[0] || null, [analytics.ticketsByQueue]);
  const topAttendant = useMemo(() => analytics.usersComparison[0] || null, [analytics.usersComparison]);

  const summaryCards = useMemo(() => {
    const { summary } = analytics;

    return [
      {
        label: "Total de mensagens",
        value: formatInteger(summary.totalMessages),
        helper: `${formatInteger(summary.inboundMessages)} recebidas • ${formatInteger(summary.outboundMessages)} enviadas`,
        icon: <ChatIcon />
      },
      {
        label: "Tickets criados",
        value: formatInteger(summary.ticketsCreated),
        helper: `Abertos ${formatInteger(summary.openTickets)} • Pendentes ${formatInteger(summary.pendingTickets)} • Fechados ${formatInteger(summary.closedTickets)}`,
        icon: <TimelineIcon />
      },
      {
        label: "Contatos ativos",
        value: formatInteger(summary.activeContacts),
        helper: `${formatInteger(summary.activeConnections)} conexões com tráfego no período`,
        icon: <GroupIcon />
      },
      {
        label: "Tempo médio de resposta",
        value: formatMinutes(summary.avgFirstResponseMinutes),
        helper: "Primeira resposta após a entrada do cliente",
        icon: <AccessTimeIcon />
      },
      {
        label: "Tempo médio na fila",
        value: formatMinutes(summary.avgWaitMinutes),
        helper: "Intervalo até o início do atendimento",
        icon: <ScheduleIcon />
      },
      {
        label: "Tempo médio de resolução",
        value: formatMinutes(summary.avgResolutionMinutes),
        helper: "Da abertura até o fechamento do ticket",
        icon: <AssessmentIcon />
      },
      {
        label: "Horário de pico",
        value: summary.peakHourLabel || "--",
        helper: `${formatInteger(summary.peakHourMessages)} mensagens no horário mais forte`,
        icon: <EqualizerIcon />
      },
      {
        label: "Dia mais forte",
        value: summary.peakWeekdayLabel || "--",
        helper: `${formatInteger(summary.peakWeekdayTickets)} tickets no melhor dia da semana`,
        icon: <ForumIcon />
      },
      {
        label: "Média de mensagens por ticket",
        value: formatDecimal(summary.avgMessagesPerTicket, 1),
        helper: `${formatInteger(summary.activeTickets)} tickets com tráfego no período`,
        icon: <PhoneInTalkIcon />
      }
    ];
  }, [analytics]);

  const messagesLineData = useMemo(
    () => ({
      labels: analytics.messagesByDay.map(item => item.label),
      datasets: [
        {
          label: "Recebidas",
          data: analytics.messagesByDay.map(item => item.inbound),
          borderColor: "#1E88E5",
          backgroundColor: "rgba(30,136,229,0.18)",
          tension: 0.25,
          fill: true
        },
        {
          label: "Enviadas",
          data: analytics.messagesByDay.map(item => item.outbound),
          borderColor: "#43A047",
          backgroundColor: "rgba(67,160,71,0.18)",
          tension: 0.25,
          fill: true
        }
      ]
    }),
    [analytics.messagesByDay]
  );

  const statusChartData = useMemo(
    () => ({
      labels: analytics.ticketsByStatus.map(item => item.label),
      datasets: [
        {
          label: "Tickets",
          data: analytics.ticketsByStatus.map(item => item.total),
          backgroundColor: ["#1E88E5", "#FB8C00", "#43A047", "#8E24AA", "#546E7A", "#EF5350"]
        }
      ]
    }),
    [analytics.ticketsByStatus]
  );

  const hourlyChartData = useMemo(
    () => ({
      labels: analytics.hourlyDistribution.map(item => item.label),
      datasets: [
        {
          label: "Recebidas",
          data: analytics.hourlyDistribution.map(item => item.inbound),
          backgroundColor: "rgba(30, 136, 229, 0.75)",
          borderRadius: 8
        },
        {
          label: "Enviadas",
          data: analytics.hourlyDistribution.map(item => item.outbound),
          backgroundColor: "rgba(67, 160, 71, 0.75)",
          borderRadius: 8
        }
      ]
    }),
    [analytics.hourlyDistribution]
  );

  const weekdayChartData = useMemo(
    () => ({
      labels: orderedWeekdays.map(item => item.label),
      datasets: [
        {
          label: "Tickets",
          data: orderedWeekdays.map(item => item.totalTickets),
          backgroundColor: "rgba(63,81,181,0.8)",
          borderRadius: 8
        },
        {
          label: "Mensagens",
          data: orderedWeekdays.map(item => item.totalMessages),
          backgroundColor: "rgba(0,188,212,0.7)",
          borderRadius: 8
        }
      ]
    }),
    [orderedWeekdays]
  );

  const queueChartData = useMemo(() => {
    const topQueues = analytics.ticketsByQueue.slice(0, 8);

    return {
      labels: topQueues.map(item => item.name),
      datasets: [
        {
          label: "Tickets",
          data: topQueues.map(item => item.totalTickets),
          backgroundColor: "rgba(255,152,0,0.78)",
          borderRadius: 10
        }
      ]
    };
  }, [analytics.ticketsByQueue]);

  const responseBucketsChartData = useMemo(
    () => ({
      labels: analytics.responseTimeBuckets.map(item => item.label),
      datasets: [
        {
          label: "Tickets",
          data: analytics.responseTimeBuckets.map(item => item.total),
          backgroundColor: ["#43A047", "#66BB6A", "#FFA726", "#FB8C00", "#EF5350", "#8E24AA"]
        }
      ]
    }),
    [analytics.responseTimeBuckets]
  );

  const conversationBucketsChartData = useMemo(
    () => ({
      labels: analytics.conversationSizeBuckets.map(item => item.label),
      datasets: [
        {
          label: "Tickets",
          data: analytics.conversationSizeBuckets.map(item => item.total),
          backgroundColor: ["#26A69A", "#29B6F6", "#5C6BC0", "#AB47BC", "#FF7043", "#8D6E63"]
        }
      ]
    }),
    [analytics.conversationSizeBuckets]
  );

  const connectionsChartData = useMemo(
    () => ({
      labels: analytics.topReceivingConnections.map(item => item.name),
      datasets: [
        {
          label: "Mensagens recebidas",
          data: analytics.topReceivingConnections.map(item => item.total),
          backgroundColor: "rgba(63,81,181,0.82)",
          borderRadius: 10
        }
      ]
    }),
    [analytics.topReceivingConnections]
  );

  const handleExportCsv = () => {
    const csvLines = [
      ["Resumo", "Valor"],
      ["Total de mensagens", analytics.summary.totalMessages],
      ["Mensagens recebidas", analytics.summary.inboundMessages],
      ["Mensagens enviadas", analytics.summary.outboundMessages],
      ["Contatos ativos", analytics.summary.activeContacts],
      ["Conexões ativas", analytics.summary.activeConnections],
      ["Tickets ativos", analytics.summary.activeTickets],
      ["Tickets criados", analytics.summary.ticketsCreated],
      ["Tickets abertos", analytics.summary.openTickets],
      ["Tickets pendentes", analytics.summary.pendingTickets],
      ["Tickets fechados", analytics.summary.closedTickets],
      ["Tempo médio de resposta (min)", analytics.summary.avgFirstResponseMinutes],
      ["Tempo médio na fila (min)", analytics.summary.avgWaitMinutes],
      ["Tempo médio de resolução (min)", analytics.summary.avgResolutionMinutes],
      ["Média de mensagens por ticket", analytics.summary.avgMessagesPerTicket],
      ["Horário de pico", analytics.summary.peakHourLabel],
      ["Mensagens no horário de pico", analytics.summary.peakHourMessages],
      ["Dia da semana mais forte", analytics.summary.peakWeekdayLabel],
      ["Tickets no dia mais forte", analytics.summary.peakWeekdayTickets],
      [],
      ["Mensagens por dia"],
      ["Data", "Recebidas", "Enviadas", "Total"],
      ...analytics.messagesByDay.map(item => [item.label, item.inbound, item.outbound, item.total]),
      [],
      ["Horários de pico"],
      ["Hora", "Recebidas", "Enviadas", "Total"],
      ...analytics.hourlyDistribution.map(item => [item.label, item.inbound, item.outbound, item.total]),
      [],
      ["Ranking por dia da semana"],
      ["Dia", "Tickets", "Mensagens", "Tempo médio de resposta (min)", "Tempo médio de resolução (min)"],
      ...orderedWeekdays.map(item => [
        item.label,
        item.totalTickets,
        item.totalMessages,
        item.avgFirstResponseMinutes,
        item.avgResolutionMinutes
      ]),
      [],
      ["Tickets por fila"],
      [
        "Fila",
        "Tickets",
        "Abertos",
        "Pendentes",
        "Fechados",
        "Recebidas",
        "Enviadas",
        "Resposta média (min)",
        "Fila média (min)",
        "Resolução média (min)"
      ],
      ...analytics.ticketsByQueue.map(item => [
        item.name,
        item.totalTickets,
        item.openTickets,
        item.pendingTickets,
        item.closedTickets,
        item.inboundMessages,
        item.outboundMessages,
        item.avgFirstResponseMinutes,
        item.avgWaitMinutes,
        item.avgResolutionMinutes
      ]),
      [],
      ["Comparação por atendente"],
      [
        "Atendente",
        "Tickets",
        "Fechados",
        "Recebidas",
        "Enviadas",
        "Resposta média (min)",
        "Fila média (min)",
        "Resolução média (min)",
        "% resolução"
      ],
      ...analytics.usersComparison.map(item => [
        item.name,
        item.tickets,
        item.closedTickets,
        item.inboundMessages,
        item.outboundMessages,
        item.avgFirstResponseMinutes,
        item.avgWaitMinutes,
        item.avgResolutionMinutes,
        item.resolutionRate
      ]),
      [],
      ["Faixas de tempo de resposta"],
      ["Faixa", "Tickets"],
      ...analytics.responseTimeBuckets.map(item => [item.label, item.total]),
      [],
      ["Complexidade das conversas"],
      ["Faixa", "Tickets"],
      ...analytics.conversationSizeBuckets.map(item => [item.label, item.total]),
      [],
      ["Top contatos que mais mandam mensagens"],
      ["Contato", "Número", "Mensagens"],
      ...analytics.topContacts.map(item => [item.name, item.number, item.total]),
      [],
      ["Top conexões que mais recebem mensagens"],
      ["Conexão", "Número", "Mensagens"],
      ...analytics.topReceivingConnections.map(item => [item.name, item.number, item.total]),
      [],
      ["Top conexões que mais enviam mensagens"],
      ["Conexão", "Número", "Mensagens"],
      ...analytics.topSendingConnections.map(item => [item.name, item.number, item.total])
    ];

    const csvContent = csvLines
      .map(line => line.map(escCsv).join(";"))
      .join("\n");

    const blob = new Blob(["\uFEFF" + csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `analitico-${dateFrom}-${dateTo}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleExportPdf = async () => {
  if (!printableRef.current) {
    return;
  }

  await new Promise(resolve => setTimeout(resolve, 1000));

  html2pdf()
    .set({
      margin: 5,
      filename: `analitico-${dateFrom}-${dateTo}.pdf`,
      image: { type: "jpeg", quality: 1 },
      html2canvas: {
        scale: 3,
        useCORS: true
      },
      jsPDF: {
        unit: "mm",
        format: "a3",
        orientation: "landscape"
      }
    })
    .from(printableRef.current)
    .save();
};

  if (user?.profile !== "admin") {
    return <ForbiddenPage />;
  }

  return (
    <MainContainer>
      <MainHeader>
        <Title>Analítico</Title>
        <MainHeaderButtonsWrapper>
          <Button variant="outlined" color="primary" startIcon={<AutorenewIcon />} onClick={fetchAnalytics}>
            Atualizar
          </Button>
          <Button variant="outlined" color="primary" startIcon={<CloudDownloadIcon />} onClick={handleExportCsv}>
            Exportar CSV
          </Button>
          <Button variant="contained" color="primary" startIcon={<PictureAsPdfIcon />} onClick={handleExportPdf}>
            Exportar PDF
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <Box className={classes.mainContainerScroll}>
        <Paper className={classes.filtersPaper} variant="outlined">
          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <TextField
                label="Data inicial"
                type="date"
                value={dateFrom}
                onChange={event => setDateFrom(event.target.value)}
                variant="outlined"
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                label="Data final"
                type="date"
                value={dateTo}
                onChange={event => setDateTo(event.target.value)}
                variant="outlined"
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <ConnectionsFilter onFiltered={items => setSelectedConnections(items.map(item => item.id))} />
            </Grid>
            <Grid item xs={12} md={6}>
              <QueueFilter onFiltered={items => setSelectedQueues(items.map(item => item.id))} />
            </Grid>
            <Grid item xs={12} md={6}>
              <UsersFilter onFiltered={items => setSelectedUsers(items.map(item => item.id))} />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="body2" color="textSecondary">
                O relatório agora mostra horários de pico, tempos médios de resposta e resolução, tickets por fila, comparação por atendente, ranking por dia da semana e faixas de complexidade das conversas.
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Button
                variant="contained"
                color="primary"
                startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <AssessmentIcon />}
                onClick={fetchAnalytics}
                disabled={loading}
              >
                {loading ? "Gerando relatório..." : "Gerar relatório"}
              </Button>
            </Grid>
          </Grid>
        </Paper>

        <div ref={printableRef}>
          {loading ? (
            <Paper className={classes.mainPaper} variant="outlined">
              <Box className={classes.loadingBox}>
                <CircularProgress />
              </Box>
            </Paper>
          ) : (
            <>
              <Grid container spacing={2}>
                {summaryCards.map(card => (
                  <Grid item xs={12} sm={6} md={4} key={card.label}>
                    <MetricCard {...card} />
                  </Grid>
                ))}
              </Grid>

              <Box mt={2}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={8}>
                    <Paper className={classes.chartPaper} variant="outlined">
                      <Typography variant="h6" className={classes.sectionTitle}>
                        <TimelineIcon fontSize="small" /> Evolução diária das mensagens
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Comparativo entre mensagens recebidas e enviadas por dia para identificar variações do volume operacional.
                      </Typography>
                      <Box className={classes.chartBox} mt={2}>
                        {analytics.messagesByDay.length ? (
                          <Line data={messagesLineData} options={chartOptions} />
                        ) : (
                          <Box className={classes.emptyBox}>Sem dados para exibir no gráfico.</Box>
                        )}
                      </Box>
                    </Paper>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <Paper className={classes.chartPaper} variant="outlined">
                      <Typography variant="h6" className={classes.sectionTitle}>
                        <AssessmentIcon fontSize="small" /> Tickets por status
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Distribuição dos tickets criados no período selecionado.
                      </Typography>
                      <Box className={classes.chartBox} mt={2}>
                        {analytics.ticketsByStatus.length ? (
                          <Doughnut
                            data={statusChartData}
                            options={{
                              responsive: true,
                              maintainAspectRatio: false,
                              plugins: { legend: { position: "bottom" } }
                            }}
                          />
                        ) : (
                          <Box className={classes.emptyBox}>Sem dados para exibir no gráfico.</Box>
                        )}
                      </Box>
                    </Paper>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Paper className={classes.chartPaper} variant="outlined">
                      <Typography variant="h6" className={classes.sectionTitle}>
                        <EqualizerIcon fontSize="small" /> Horários de pico
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Leitura por faixa horária para mostrar quando a operação recebe e responde mais mensagens.
                      </Typography>
                      <Box className={classes.chartBox} mt={2}>
                        {analytics.hourlyDistribution.length ? (
                          <Bar data={hourlyChartData} options={chartOptions} />
                        ) : (
                          <Box className={classes.emptyBox}>Sem dados para exibir no gráfico.</Box>
                        )}
                      </Box>
                    </Paper>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Paper className={classes.chartPaper} variant="outlined">
                      <Typography variant="h6" className={classes.sectionTitle}>
                        <ForumIcon fontSize="small" /> Ranking por dia da semana
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Relação entre tickets e mensagens para encontrar os dias mais fortes da operação.
                      </Typography>
                      <Box className={classes.chartBox} mt={2}>
                        {orderedWeekdays.length ? (
                          <Bar data={weekdayChartData} options={chartOptions} />
                        ) : (
                          <Box className={classes.emptyBox}>Sem dados para exibir no gráfico.</Box>
                        )}
                      </Box>
                    </Paper>
                  </Grid>

                  <Grid item xs={12} md={8}>
                    <Paper className={classes.chartPaper} variant="outlined">
                      <Typography variant="h6" className={classes.sectionTitle}>
                        <AssessmentIcon fontSize="small" /> Tickets por fila
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        As filas com mais tickets ajudam a enxergar concentração de demanda e gargalos operacionais.
                      </Typography>
                      <Box className={classes.chartBox} mt={2}>
                        {analytics.ticketsByQueue.length ? (
                          <Bar
                            data={queueChartData}
                            options={{
                              ...chartOptions,
                              indexAxis: "y",
                              plugins: { legend: { display: false } }
                            }}
                          />
                        ) : (
                          <Box className={classes.emptyBox}>Sem dados para exibir no gráfico.</Box>
                        )}
                      </Box>
                    </Paper>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <Paper className={classes.chartPaper} variant="outlined">
                      <Typography variant="h6" className={classes.sectionTitle}>
                        <AccessTimeIcon fontSize="small" /> Faixas de resposta
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Distribuição dos tickets por velocidade da primeira resposta.
                      </Typography>
                      <Box className={classes.chartBox} mt={2}>
                        {analytics.responseTimeBuckets.length ? (
                          <Doughnut
                            data={responseBucketsChartData}
                            options={{
                              responsive: true,
                              maintainAspectRatio: false,
                              plugins: { legend: { position: "bottom" } }
                            }}
                          />
                        ) : (
                          <Box className={classes.emptyBox}>Sem dados para exibir no gráfico.</Box>
                        )}
                      </Box>
                    </Paper>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Paper className={classes.chartPaper} variant="outlined">
                      <Typography variant="h6" className={classes.sectionTitle}>
                        <ChatIcon fontSize="small" /> Complexidade das conversas
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Agrupamento pela quantidade de mensagens em cada ticket para separar conversas rápidas de casos longos.
                      </Typography>
                      <Box className={classes.chartBox} mt={2}>
                        {analytics.conversationSizeBuckets.length ? (
                          <Doughnut
                            data={conversationBucketsChartData}
                            options={{
                              responsive: true,
                              maintainAspectRatio: false,
                              plugins: { legend: { position: "bottom" } }
                            }}
                          />
                        ) : (
                          <Box className={classes.emptyBox}>Sem dados para exibir no gráfico.</Box>
                        )}
                      </Box>
                    </Paper>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Paper className={classes.chartPaper} variant="outlined">
                      <Typography variant="h6" className={classes.sectionTitle}>
                        <PhoneInTalkIcon fontSize="small" /> Conexões que mais recebem mensagens
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Ranking visual das conexões com maior volume de entrada para ajudar no balanceamento entre canais.
                      </Typography>
                      <Box className={classes.chartBox} mt={2}>
                        {analytics.topReceivingConnections.length ? (
                          <Bar
                            data={connectionsChartData}
                            options={{
                              ...chartOptions,
                              indexAxis: "y",
                              plugins: { legend: { display: false } }
                            }}
                          />
                        ) : (
                          <Box className={classes.emptyBox}>Sem dados para exibir no gráfico.</Box>
                        )}
                      </Box>
                    </Paper>
                  </Grid>
                </Grid>
              </Box>

              <Box mt={2}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <RankingTable
                      title="Tickets por fila"
                      subtitle="Visão consolidada por fila com volume, status e tempos médios."
                      columns={[
                        { field: "position", title: "#", render: (_, index) => index + 1 },
                        { field: "name", title: "Fila" },
                        { field: "totalTickets", title: "Tickets" },
                        {
                          field: "statuses",
                          title: "Status",
                          render: row => `A ${row.openTickets} • P ${row.pendingTickets} • F ${row.closedTickets}`
                        },
                        {
                          field: "avgFirstResponseMinutes",
                          title: "Resposta média",
                          render: row => formatMinutes(row.avgFirstResponseMinutes)
                        },
                        {
                          field: "avgResolutionMinutes",
                          title: "Resolução média",
                          render: row => formatMinutes(row.avgResolutionMinutes)
                        }
                      ]}
                      rows={analytics.ticketsByQueue}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <RankingTable
                      title="Comparação por atendente"
                      subtitle="Eficiência por atendente com tickets, mensagens e taxa de fechamento."
                      columns={[
                        { field: "position", title: "#", render: (_, index) => index + 1 },
                        { field: "name", title: "Atendente" },
                        { field: "tickets", title: "Tickets" },
                        { field: "closedTickets", title: "Fechados" },
                        {
                          field: "avgFirstResponseMinutes",
                          title: "1ª resposta",
                          render: row => formatMinutes(row.avgFirstResponseMinutes)
                        },
                        {
                          field: "resolutionRate",
                          title: "% resolução",
                          render: row => formatPercent(row.resolutionRate)
                        }
                      ]}
                      rows={analytics.usersComparison}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <RankingTable
                      title="Ranking por dia da semana"
                      subtitle="Leitura rápida do melhor e do pior dia do período filtrado."
                      columns={[
                        { field: "position", title: "#", render: (_, index) => index + 1 },
                        { field: "label", title: "Dia" },
                        { field: "totalTickets", title: "Tickets" },
                        { field: "totalMessages", title: "Mensagens" },
                        {
                          field: "avgFirstResponseMinutes",
                          title: "Resposta média",
                          render: row => formatMinutes(row.avgFirstResponseMinutes)
                        }
                      ]}
                      rows={analytics.weekdayRanking}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <RankingTable
                      title="Faixas de primeira resposta"
                      subtitle="Quanto dos tickets caiu em cada janela de SLA de resposta."
                      columns={[
                        { field: "position", title: "#", render: (_, index) => index + 1 },
                        { field: "label", title: "Faixa" },
                        { field: "total", title: "Tickets" }
                      ]}
                      rows={analytics.responseTimeBuckets}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <RankingTable
                      title="Números que mais mandam mensagens"
                      subtitle="Top 10 contatos com maior volume de mensagens recebidas pela operação."
                      columns={[
                        { field: "position", title: "#", render: (_, index) => index + 1 },
                        { field: "name", title: "Contato" },
                        { field: "number", title: "Número" },
                        { field: "total", title: "Mensagens" }
                      ]}
                      rows={analytics.topContacts}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <RankingTable
                      title="Conexões que mais recebem mensagens"
                      subtitle="Top 10 conexões considerando somente mensagens recebidas."
                      columns={[
                        { field: "position", title: "#", render: (_, index) => index + 1 },
                        { field: "name", title: "Conexão" },
                        { field: "number", title: "Número" },
                        { field: "total", title: "Recebidas" }
                      ]}
                      rows={analytics.topReceivingConnections}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <RankingTable
                      title="Conexões que mais enviam mensagens"
                      subtitle="Top 10 conexões considerando mensagens enviadas."
                      columns={[
                        { field: "position", title: "#", render: (_, index) => index + 1 },
                        { field: "name", title: "Conexão" },
                        { field: "number", title: "Número" },
                        { field: "total", title: "Enviadas" }
                      ]}
                      rows={analytics.topSendingConnections}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <RankingTable
                      title="Atendentes com maior volume"
                      subtitle="Recorte rápido dos atendentes com mais tickets e mais saída de mensagens."
                      columns={[
                        { field: "position", title: "#", render: (_, index) => index + 1 },
                        { field: "name", title: "Atendente" },
                        { field: "tickets", title: "Tickets" },
                        { field: "inboundMessages", title: "Recebidas" },
                        { field: "outboundMessages", title: "Enviadas" },
                        {
                          field: "avgFirstResponseMinutes",
                          title: "1ª resposta",
                          render: row => formatMinutes(row.avgFirstResponseMinutes)
                        }
                      ]}
                      rows={analytics.topUsers}
                    />
                  </Grid>
                </Grid>
              </Box>

              <Box mt={2}>
                <Paper className={classes.mainPaper} variant="outlined">
                  <Typography variant="h6" className={classes.sectionTitle}>
                    <PersonIcon fontSize="small" /> Resumo executivo
                  </Typography>
                  <Box className={classes.insightCard}>
                    <Typography variant="body1">
                      No período selecionado, a operação movimentou <strong>{formatInteger(analytics.summary.totalMessages)}</strong> mensagens e abriu <strong>{formatInteger(analytics.summary.ticketsCreated)}</strong> tickets. O tempo médio de primeira resposta ficou em <strong>{formatMinutes(analytics.summary.avgFirstResponseMinutes)}</strong>, enquanto o tempo médio de resolução fechou em <strong>{formatMinutes(analytics.summary.avgResolutionMinutes)}</strong>.
                    </Typography>
                    <Typography variant="body1" className={classes.insightLine}>
                      O pico aconteceu em <strong>{analytics.summary.peakHourLabel || "--"}</strong>, com <strong>{formatInteger(analytics.summary.peakHourMessages)}</strong> mensagens, e o melhor dia da semana foi <strong>{analytics.summary.peakWeekdayLabel || "--"}</strong>, com <strong>{formatInteger(analytics.summary.peakWeekdayTickets)}</strong> tickets.
                    </Typography>
                    <Typography variant="body1" className={classes.insightLine}>
                      {topQueue ? (
                        <>
                          A fila com mais demanda foi <strong>{topQueue.name}</strong>, somando <strong>{formatInteger(topQueue.totalTickets)}</strong> tickets e resposta média de <strong>{formatMinutes(topQueue.avgFirstResponseMinutes)}</strong>.
                        </>
                      ) : (
                        <>Nenhuma fila teve volume suficiente para análise no filtro atual.</>
                      )}
                    </Typography>
                    <Typography variant="body1" className={classes.insightLine}>
                      {topAttendant ? (
                        <>
                          Entre os atendentes, <strong>{topAttendant.name}</strong> liderou o volume com <strong>{formatInteger(topAttendant.tickets)}</strong> tickets e taxa de resolução de <strong>{formatPercent(topAttendant.resolutionRate)}</strong>.
                        </>
                      ) : (
                        <>Nenhum atendente teve volume suficiente para comparação no filtro atual.</>
                      )}
                    </Typography>
                  </Box>
                </Paper>
              </Box>
            </>
          )}
        </div>
      </Box>
    </MainContainer>
  );
};

export default Analitico;
